// University of North Texas: public college page (#2839), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UNT's Common Data Set 2025-2026 (Office of Institutional Research and
//    Effectiveness; the newest edition on its Common Data Set page), read
//    from the rendered PDF pages because parts of its text layer are
//    garbled: C1 applicants, admits and enrollees for Fall 2025 with the
//    residency table, C8 test policy for Fall 2027, C21/C22 (no early
//    decision and no early action plan). Quirk: the C1 table by sex gives
//    41,250 applicants (19,128 men, 22,122 women); the residency table
//    beside it totals 41,247, three fewer. The page uses the C1 total; the
//    residency rows are shown as the CDS gives them. C8E gives no score date.
//  - unt.edu/admissions/freshman: Admission Requirements (guaranteed
//    admission, test-score-optional), Deadlines & Fees (the Fall deadline
//    table; it carries no year, so it is read as the deadlines for the
//    application now open, fall 2027 entry), Steps to Apply (SAT or ACT
//    optional, rolling decisions).
//  - UNT College of Music, Undergraduate Admissions (the separate music
//    application and audition, with its Fall 2027 dates).
//  - UNT Financial Aid & Scholarships, Undergraduate Cost of Attendance,
//    2026-2027, on campus. Departure: the CDS G1 box says UNT's 2026-2027
//    costs were not available when it was filed, so the aid office's
//    2026-2027 budget is used. That page gives a resident total only; the
//    out-of-state column swaps in its footnoted non-resident tuition and
//    fees ($24,932) and keeps the other items, so its total is the sum of
//    those items, not a figure UNT prints.
//  - College Scorecard API, school id 227216 (net price by family income,
//    in-state).
// No announced change for fall 2028 entry was found on the admissions pages.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UNT Common Data Set 2025-2026',
        url: 'https://institutionalresearch.unt.edu/cds_univnorthtx_2025-2026.pdf',
        read: '2026-10-01',
    },
    requirements: {
        label: 'UNT Admissions, Freshman Admission Requirements',
        url: 'https://www.unt.edu/admissions/freshman/admission-requirements.html',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'UNT Admissions, Freshman Deadlines & Fees',
        url: 'https://www.unt.edu/admissions/freshman/deadlines-fees.html',
        read: '2026-10-01',
    },
    steps: {
        label: 'UNT Admissions, Steps to Apply',
        url: 'https://www.unt.edu/admissions/freshman/steps-to-apply.html',
        read: '2026-10-01',
    },
    music: {
        label: 'UNT College of Music, Undergraduate Admissions',
        url: 'https://music.unt.edu/admissions/undergraduate',
        read: '2026-10-01',
    },
    coa: {
        label: 'UNT Financial Aid & Scholarships, Undergraduate Cost of Attendance 2026-2027',
        url: 'https://financialaid.unt.edu/undergraduate.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?227216-University-of-North-Texas',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-north-texas',
    kbId: 'university_of_north_texas',
    name: 'University of North Texas',
    shortName: 'UNT',
    aliases: ['UNT', 'North Texas'],
    location: 'Denton, TX',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UNT identity guide colors); decoration only.
    branding: {
        primary: '#00853E',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_north_texas_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 41250,
        admitted: 29242,
        enrolled: 6388,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 36749, admitted: 26788 },
            { group: 'Out-of-state', applied: 2245, admitted: 1537 },
            { group: 'International', applied: 2253, admitted: 917 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table (its applicant total is 41,247, three fewer than the C1 total)',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UNT\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'UNT has no early decision and no early action plan, so the Common Data Set gives no early round counts.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to UNT as a whole. Two things work differently:',
        rows: [
            { school: 'Guaranteed admission', requirement: 'UNT guarantees admission if you meet one of these and complete its application: top 30% of your high school class, a cumulative unweighted GPA of 3.25 or higher, an unweighted 3.0 in at least 12 semester hours of dual-enrollment courses, an unweighted 3.0 in your core high school courses, or an ACT of 22 or SAT of 1100 (official scores required).', source: 'requirements' },
            { school: 'College of Music', requirement: 'A second application to the College of Music through Acceptd after you apply to UNT, and an audition or portfolio. For fall 2027, apply by November 1 for a live audition in December; for January or February dates, UNT gives preference to applications received by December 1. A video audition is available if you cannot attend.', source: 'music' },
        ],
        source: 'requirements',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application deadline', value: 'July 31' },
        rows: [
            { plan: 'Fall application', deadline: 'July 31', decision: 'Rolling', binding: false },
        ],
        note: 'Apply and be admitted by March 1 to be considered for the UNT Excellence Scholarship; UNT keeps reviewing applications after that date. International applicants apply by July 15. The application fee is $75 for U.S. citizens and residents, $85 for international applicants, and fee waivers are accepted.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'UNT is test-score-optional: it encourages you to send SAT or ACT scores, but they are optional, and the writing section is not required. You may be considered without a score if your cumulative unweighted high school GPA is above 3.0. Scores can still help with admission decisions, testing exemptions and scholarships.',
        source: 'steps',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required but considered if submitted for fall 2027 applicants.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'requirements',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [12092, 24932] },
            { item: 'Housing and food (on campus)', amounts: [12106, 12106] },
            { item: 'Books and supplies', amounts: [1120, 1120] },
            { item: 'Transportation', amounts: [2352, 2352] },
            { item: 'Personal', amounts: [2028, 2028] },
        ],
        totals: [29698, 42538],
        note: 'For an undergraduate living on campus, 15 hours a semester, fall and spring. UNT prints the in-state total; the out-of-state total is the sum of the same items with its non-resident tuition and fees. Federal loan fees are added to the budget for students who borrow.',
        source: 'coa',
        netPrice: {
            average: 15358,
            byIncome: [
                { income: '$0 to $30,000', amount: 12866 },
                { income: '$30,001 to $48,000', amount: 12814 },
                { income: '$48,001 to $75,000', amount: 12645 },
                { income: '$75,001 to $110,000', amount: 15983 },
                { income: 'Over $110,000', amount: 23964 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UNT\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
